import { useState, useRef, useLayoutEffect } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

interface Props<T> {
  items: T[];
  rowHeight: number; // fixed row height incl. gap
  overscan?: number;
  render: (item: T, index: number) => ComponentChildren;
  class?: string;
}

/** Fixed-height windowed list — keeps the roster at 60fps with thousands
 *  of rows without dragging in a virtualization dependency. */
export function VirtualList<T>({ items, rowHeight, overscan = 6, render, class: cls }: Props<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState({ start: 0, end: 30 });

  const update = () => {
    const el = ref.current;
    if (!el) return;
    const start = Math.max(0, Math.floor(el.scrollTop / rowHeight) - overscan);
    const visible = Math.ceil(el.clientHeight / rowHeight) + overscan * 2;
    setRange((r) =>
      r.start === start && r.end === start + visible ? r : { start, end: start + visible }
    );
  };

  useLayoutEffect(update, [items.length]);

  const slice = items.slice(range.start, range.end);

  return (
    <div ref={ref} class={cls} onScroll={update} style={{ overflowY: 'auto' }}>
      <div style={{ height: `${items.length * rowHeight}px`, position: 'relative' }}>
        {slice.map((item, i) => (
          <div
            key={(item as { id?: string }).id ?? range.start + i}
            style={{
              position: 'absolute',
              top: `${(range.start + i) * rowHeight}px`,
              left: 0,
              right: 0,
              height: `${rowHeight}px`,
            }}
          >
            {render(item, range.start + i)}
          </div>
        ))}
      </div>
    </div>
  );
}
